import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import EditableImage from '@/components/base/EditableImage';
import { blogPreviewPosts } from '@/mocks/home';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const allPosts = [
  ...blogPreviewPosts,
  {
    id: 4,
    title: 'Como se preparar para o ensaio fotográfico perfeito',
    excerpt: 'Guia completo com dicas de roupa, maquiagem e locais para o seu ensaio.',
    date: '10 Abril 2026',
    image: 'https://readdy.ai/api/search-image?query=photo%20shoot%20preparation%20flat%20lay%20accessories%20camera%20flowers%20makeup%20elegant%20styling%20soft%20natural%20light%20warm%20tones%20professional%20photography%20editorial%20style&width=500&height=350&seq=33&orientation=landscape',
    category: 'Dicas',
  },
  {
    id: 5,
    title: 'A importância da luz natural na fotografia de casamento',
    excerpt: 'Entenda como a luz pode transformar completamente suas fotos.',
    date: '28 Março 2026',
    image: 'https://readdy.ai/api/search-image?query=wedding%20photography%20natural%20light%20beautiful%20bride%20near%20window%20soft%20golden%20light%20romantic%20atmosphere%20professional%20photography%20warm%20tones%20artistic%20composition&width=500&height=350&seq=34&orientation=landscape',
    category: 'Técnica',
  },
  {
    id: 6,
    title: 'Eventos corporativos: como documentar com elegância',
    excerpt: 'Estratégias para capturar a essência dos eventos empresariais.',
    date: '15 Março 2026',
    image: 'https://readdy.ai/api/search-image?query=corporate%20event%20photography%20elegant%20conference%20speaker%20on%20stage%20modern%20venue%20professional%20lighting%20sophisticated%20atmosphere%20warm%20ambient%20light%20documentary%20style&width=500&height=350&seq=35&orientation=landscape',
    category: 'Corporativo',
  },
];

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const categories = ['Todos', 'Dicas', 'Tendências', 'Técnica', 'Corporativo'];

  const filteredPosts = activeCategory === 'Todos'
    ? allPosts
    : allPosts.filter((post) => post.category === activeCategory);

  const schema = [
    {
      '@type': 'Blog',
      name: 'Blog Jessica Bonini Fotografia',
      description: 'Dicas, tendências e bastidores do mundo da fotografia profissional de casamentos, pré-wedding e eventos.',
      url: `${SITE_URL}/blog`,
      inLanguage: 'pt-BR',
      blogPost: allPosts.map((post) => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        image: post.image,
        url: `${SITE_URL}/blog`,
      })),
    },
    {
      '@type': 'WebPage',
      name: 'Blog de Fotografia',
      description: 'Inspiração e conhecimento sobre fotografia profissional de casamentos, pré-wedding e eventos corporativos.',
      url: `${SITE_URL}/blog`,
    },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Blog | Jessica Bonini Fotografia - Dicas & Inspiração"
        description="Dicas, tendências e bastidores do mundo da fotografia profissional. Inspiração para casamentos, pré-wedding, sessão família e eventos corporativos."
        keywords="blog fotografia, dicas casamento, fotografia tendências, ensaio fotográfico dicas, fotografia corporativa"
        canonicalPath="/blog"
        ogType="website"
        schema={schema}
      />
      <Navbar />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-cream">
          <div className="px-6 md:px-10 lg:px-16 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-warm-600 mb-4 block">
              Blog
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dark font-normal leading-tight mb-6">
              Inspiração & <span className="italic">conhecimento</span>
            </h1>
            <p className="text-dark/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Dicas, tendências e bastidores do mundo da fotografia profissional.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white">
          <div className="px-6 md:px-10 lg:px-16">
            <div className="flex flex-wrap justify-center gap-3 mb-12 md:mb-16">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeCategory === cat
                      ? 'bg-dark text-white'
                      : 'bg-cream text-dark/70 hover:bg-warm-100'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredPosts.map((post) => (
                <article key={post.id} className="group cursor-pointer">
                  <div className="relative h-52 md:h-56 rounded-xl md:rounded-2xl overflow-hidden mb-5">
                    <EditableImage
                      imageKey={`blog-post-${post.id}`}
                      src={post.image}
                      alt={post.title}
                      containerClassName="w-full h-full"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    >
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-dark">
                          {post.category}
                        </span>
                      </div>
                    </EditableImage>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-dark/50 mb-3">
                    <span>{post.date}</span>
                    <span className="w-1 h-1 rounded-full bg-dark/30" />
                    <span>5 min de leitura</span>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-dark font-normal leading-snug mb-3 group-hover:text-warm-700 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-dark/60 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-warm-700 group-hover:text-warm-900 transition-colors">
                    Ler mais
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}